/**
 * wayai replicate [hub] [--label <l>] — clone a hub into a new sibling preview
 *
 * The SOURCE hub is derived from the workspace (or --hub):
 *   - --hub <uuid|folder-name> → that hub (preview OR production)
 *   - cwd inside a hub folder → that hub
 *   - exactly one preview in the hubs dir → it
 *
 * Replicating from a production hub creates a fresh preview off production;
 * replicating from a preview creates a sibling of the same production parent.
 * The new preview is materialized into its own `wayai-ws/hubs/<hub>--<label>/`
 * folder. `--label` disambiguates the sibling (server-owned; see `wayai relabel`).
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import { requireAuth } from '../lib/auth.js';
import { ApiClient } from '../lib/api-client.js';
import { materializeHubFolder } from '../lib/hub-materializer.js';
import { resolveHubTarget } from './pull.js';
import { printLocalFileChanges } from './push.js';
import {
  autoRenameHubFolder,
  findGitRoot,
  resolveHubFolder,
  resolveWorkspaceDir,
} from '../lib/workspace.js';
import { warnLayoutOnce } from '../lib/layout.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { seedScopeIfEmpty } from '../lib/worktree-scope.js';
import { PREVIEW_LABEL_MAX_LENGTH } from '@wayai/core/contracts';

interface ParsedArgs {
  label?: string;
  hubSelector?: string;
}

function parseArgs(args: string[]): ParsedArgs {
  let label: string | undefined;
  let hubSelector: string | undefined;

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--label' && args[i + 1]) {
      label = args[++i];
    } else if (arg === '--hub' && args[i + 1]) {
      hubSelector = args[++i];
    } else if (!arg.startsWith('-') && hubSelector === undefined) {
      // Positional source selector (uuid or folder name), same as --hub.
      hubSelector = arg;
    }
  }

  return { label, hubSelector };
}

export async function replicateCommand(args: string[]): Promise<void> {
  const { label, hubSelector } = parseArgs(args);

  const normalizedLabel = label?.trim() || undefined;
  if (normalizedLabel && normalizedLabel.length > PREVIEW_LABEL_MAX_LENGTH) {
    console.error(`--label must be ${PREVIEW_LABEL_MAX_LENGTH} characters or fewer.`);
    process.exit(1);
  }

  const repoConfig = requireRepoConfig();
  const { organization_id: organizationId } = repoConfig;

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  const workspaceDir = resolveWorkspaceDir();
  const gitRoot = findGitRoot();
  if (gitRoot) warnLayoutOnce(gitRoot);

  // Resolve the SOURCE hub (reuses pull's resolver: --hub | enclosing | sole
  // preview; also accepts a UUID for a hub not present locally). Production is a
  // valid source — `replicatePreviewHub` accepts it.
  const { hubId: sourceHubId } = resolveHubTarget(workspaceDir, hubSelector);

  console.log(
    normalizedLabel
      ? `Replicating hub ${sourceHubId} into a new preview "${normalizedLabel}"...`
      : `Replicating hub ${sourceHubId} into a new preview...`,
  );
  const { data } = await client.replicatePreview(sourceHubId, normalizedLabel ?? null);
  const previewHubId = data.preview_hub_id;
  console.log(`New preview hub created: ${previewHubId}`);

  // Materialize the new preview into its own folder (same first-pull write path
  // as `wayai pull`). It is a brand-new hub, so no scope entry can conflict.
  const payload = await client.pull(previewHubId, organizationId);
  let hubFolder = resolveHubFolder(
    workspaceDir,
    previewHubId,
    payload.hub.name,
    payload.hub_environment,
    payload.preview_label,
    payload.branch_name,
  );
  // A fresh replicate materializes a brand-new folder — everything is "new", so listing it all
  // is noise; only when the target folder already exists do we announce the per-file delta.
  const folderPreExisted = fs.existsSync(hubFolder);
  fs.mkdirSync(path.dirname(hubFolder), { recursive: true });
  const delta = await materializeHubFolder(hubFolder, payload);
  hubFolder = autoRenameHubFolder(
    hubFolder,
    payload.hub.name,
    payload.hub_environment,
    payload.hub_id,
    payload.preview_label,
    payload.branch_name,
  );

  // Seed the hubs axis with the new preview only if it is empty; otherwise leave
  // the existing scope alone — widening is `wayai use --add`, never automatic.
  seedScopeIfEmpty('hubs', previewHubId);

  if (folderPreExisted) printLocalFileChanges(delta);
  console.log(`Preview written to ${path.basename(hubFolder)}`);
  console.log(`  Switch to it with: wayai use ${path.basename(hubFolder)}`);
}
